/**
 * Pure completion-form rules (spec §2), shared by the admin modal and the
 * guest page. The server mirrors `validateCompletion`; keep the messages in
 * `WORK_ORDER_LABELS` so every surface says the same thing.
 */
import { WORK_ORDER_LABELS } from '../../workOrderSections';

export interface CompletionConditionInput {
    /** 0–10 (0 = replace now), or null until the slider has been touched. */
    value: number | null;
    comment?: string;
}

export interface CompletionDraftValues {
    comment: string;
    conditions: Record<string, CompletionConditionInput>;
    actualCost: number | null;
    actualCostInvestmentPercentage: number | null;
}

export const EMPTY_COMPLETION_DRAFT: CompletionDraftValues = {
    comment: '',
    conditions: {},
    actualCost: null,
    actualCostInvestmentPercentage: null,
};

export interface CompletionRules {
    isOpen: boolean;
    requiresPhoto: boolean;
    requiresComment: boolean;
    requiresConditionUpdate: boolean;
    /** Linked component keys; every one needs a condition when required. */
    componentKeys: string[];
    afterPhotoCount: number;
}

export const CONDITION_MIN = 0;
export const CONDITION_MAX = 10;

function isConditionInRange(value: number): boolean {
    return Number.isFinite(value) && value >= CONDITION_MIN && value <= CONDITION_MAX;
}

/**
 * Returns the Swedish validation messages that block submission. An empty
 * array means the draft can be submitted.
 */
export function validateCompletion(values: CompletionDraftValues, rules: CompletionRules): string[] {
    const errors: string[] = [];

    if (!rules.isOpen) {
        errors.push(WORK_ORDER_LABELS.validationNotOpen);
    }
    if (rules.requiresPhoto && rules.afterPhotoCount === 0) {
        errors.push(WORK_ORDER_LABELS.validationPhotoRequired);
    }
    if (rules.requiresComment && !values.comment.trim()) {
        errors.push(WORK_ORDER_LABELS.validationCommentRequired);
    }
    if (rules.requiresConditionUpdate) {
        const entries = rules.componentKeys.map(key => values.conditions[key]?.value);
        if (entries.some(value => value === null || value === undefined)) {
            errors.push(WORK_ORDER_LABELS.validationConditionsRequired);
        }
        if (entries.some(value => typeof value === 'number' && !isConditionInRange(value))) {
            errors.push(WORK_ORDER_LABELS.validationConditionRange);
        }
    }

    return errors;
}

/**
 * Maps a 0.1–10 condition to the remaining years of a component whose type
 * has an expected lifetime (`getLifetime(specificType)?.expectedLifetimeYears`).
 * Same formula as the legacy completion modal: round(condition / 10 * lifetime).
 * Returns null when the lifetime is unknown.
 */
export function estimateYearsLeft(conditionValue: number, expectedLifetimeYears: number | null): number | null {
    if (expectedLifetimeYears === null || !Number.isFinite(expectedLifetimeYears) || expectedLifetimeYears <= 0) {
        return null;
    }
    if (!Number.isFinite(conditionValue)) return null;
    return Math.round((conditionValue / 10) * expectedLifetimeYears);
}

/** "Mindre än 1 år kvar" below one year, otherwise "Ca N år kvar". */
export function yearsLeftLabel(years: number): string {
    return years < 1 ? 'Mindre än 1 år kvar' : WORK_ORDER_LABELS.conditionYearsLeft(years);
}
